import React, { useState } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from './ui/card';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Label } from './ui/label';
import { Textarea } from './ui/textarea';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from './ui/select';
import { toast } from 'sonner';
import axios from 'axios';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const ReportIssue = ({ phoneModels }) => {
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    phone: '',
    phoneBrand: '',
    phoneModel: '',
    issue: '',
    preferredTime: ''
  });
  const [isSubmitting, setIsSubmitting] = useState(false);

  const handleInputChange = (field, value) => {
    setFormData(prev => ({ ...prev, [field]: value }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    // Validation
    if (!formData.name || !formData.phone || !formData.issue) {
      toast.error('Please fill in all required fields');
      return;
    }

    setIsSubmitting(true);

    try {
      const response = await axios.post(`${API}/repair-request`, formData);
      
      if (response.data.success) {
        toast.success('Request submitted successfully!', {
          description: response.data.message
        });

        // Reset form
        setFormData({
          name: '',
          email: '',
          phone: '',
          phoneBrand: '',
          phoneModel: '',
          issue: '',
          preferredTime: ''
        });
      }
    } catch (error) {
      console.error('Error submitting repair request:', error);
      toast.error('Failed to submit request', {
        description: 'Please try again or contact us directly at 571-294-2187'
      });
    } finally {
      setIsSubmitting(false);
    }
  };

  const selectedBrandData = phoneModels.find(p => p.id.toString() === formData.phoneBrand);

  return (
    <div className="py-16 px-4 bg-white">
      <div className="max-w-4xl mx-auto">
        <div className="text-center mb-12">
          <h2 className="text-3xl font-bold text-gray-900 mb-4">
            Tell us what's wrong
          </h2>
          <p className="text-gray-600 max-w-2xl mx-auto">
            Describe your phone issue and we'll get back to you with a solution. 
            Our experts will contact you to schedule a convenient pickup time.
          </p>
        </div>

        <Card className="shadow-sm">
          <CardHeader>
            <CardTitle>Report Your Phone Issue</CardTitle>
          </CardHeader>
          <CardContent>
            <form onSubmit={handleSubmit} className="space-y-6">
              {/* Contact Information */}
              <div className="grid md:grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label htmlFor="name">Full Name *</Label>
                  <Input
                    id="name"
                    placeholder="John Doe"
                    value={formData.name}
                    onChange={(e) => handleInputChange('name', e.target.value)}
                    required
                  />
                </div>
                <div className="space-y-2">
                  <Label htmlFor="phone">Phone Number *</Label>
                  <Input
                    id="phone"
                    type="tel"
                    placeholder="+1 (555) 123-4567"
                    value={formData.phone}
                    onChange={(e) => handleInputChange('phone', e.target.value)}
                    required
                  />
                </div>
              </div>

              <div className="space-y-2">
                <Label htmlFor="email">Email Address</Label>
                <Input
                  id="email"
                  type="email"
                  placeholder="john.doe@example.com"
                  value={formData.email}
                  onChange={(e) => handleInputChange('email', e.target.value)}
                />
              </div>

              {/* Phone Details */}
              <div className="grid md:grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label htmlFor="phone-brand">Phone Brand</Label>
                  <Select 
                    value={formData.phoneBrand} 
                    onValueChange={(value) => {
                      handleInputChange('phoneBrand', value);
                      handleInputChange('phoneModel', '');
                    }}
                  >
                    <SelectTrigger id="phone-brand">
                      <SelectValue placeholder="Select brand" />
                    </SelectTrigger>
                    <SelectContent>
                      {phoneModels.map(brand => (
                        <SelectItem key={brand.id} value={brand.id.toString()}>
                          {brand.brand}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>

                <div className="space-y-2">
                  <Label htmlFor="phone-model">Phone Model</Label>
                  <Select 
                    value={formData.phoneModel} 
                    onValueChange={(value) => handleInputChange('phoneModel', value)}
                    disabled={!formData.phoneBrand}
                  >
                    <SelectTrigger id="phone-model">
                      <SelectValue placeholder="Select model" />
                    </SelectTrigger>
                    <SelectContent>
                      {selectedBrandData?.models.map((model, index) => (
                        <SelectItem key={index} value={model}>
                          {model}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
              </div>

              {/* Issue Description */}
              <div className="space-y-2">
                <Label htmlFor="issue">Describe the Issue *</Label>
                <Textarea
                  id="issue"
                  placeholder="Please describe what's wrong with your phone in detail... (e.g., Screen is cracked, Battery drains quickly, Phone won't charge, etc.)"
                  value={formData.issue}
                  onChange={(e) => handleInputChange('issue', e.target.value)}
                  rows={5}
                  required
                  className="resize-none"
                />
              </div>

              {/* Preferred Pickup Time */}
              <div className="space-y-2">
                <Label htmlFor="preferred-time">Preferred Pickup Time</Label>
                <Select 
                  value={formData.preferredTime} 
                  onValueChange={(value) => handleInputChange('preferredTime', value)}
                >
                  <SelectTrigger id="preferred-time">
                    <SelectValue placeholder="Select preferred time" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="morning">Morning (8AM - 12PM)</SelectItem>
                    <SelectItem value="afternoon">Afternoon (12PM - 4PM)</SelectItem>
                    <SelectItem value="evening">Evening (4PM - 8PM)</SelectItem>
                    <SelectItem value="flexible">Flexible - Anytime</SelectItem>
                  </SelectContent>
                </Select>
              </div>

              <Button 
                type="submit"
                className="w-full bg-blue-600 hover:bg-blue-700 transition-colors duration-200"
                size="lg"
                disabled={isSubmitting}
              >
                {isSubmitting ? 'Submitting...' : 'Submit Request'}
              </Button>

              <p className="text-sm text-gray-500 text-center">
                * Required fields. We'll contact you within 24 hours to confirm your pickup.
              </p>
            </form>
          </CardContent>
        </Card>
      </div>
    </div>
  );
};

export default ReportIssue;
